import axios from "axios";
import { SEND_CONTRACT_URL, get_request_config } from "@/utils/system_constants/constant";
import { create_populated_document_preview } from "./esigning_setup_service_helpers";
import {
    build_send_contract_payload,
    get_send_contract_failure_message,
    get_send_contract_success_message,
    has_failed_response,
    resolve_send_contract_error
} from "./esigning_setup_computed_helpers";
import {
    get_step_navigation_rule,
    has_saved_signer_configuration,
    should_skip_request_step_navigation
} from "./esigning_setup_request_summary_utils";
import {
    hide_system_loader_after_render,
    reset_signing_request_state,
    show_system_loader
} from "./esigning_setup_methods_utils";

async function open_selected_request_step(vm, step_number) {
    const step_actions = {
        1: () => vm.go_to_document_preview(),
        2: () => vm.go_to_signers_order(),
        3: async () => {
            if (vm.current_request_step === 2 && !has_saved_signer_configuration(vm)) {
                await vm.handle_signers_continue();
            } else {
                await vm.go_to_review_send();
            }
        }
    };
    const selected_action = Reflect.get(step_actions, step_number);
    if (typeof selected_action === "function") {
        await selected_action();
    }
}

export const esigning_setup_request_methods = {
    close_new_signing_request() {
        this.show_new_signing_request = false;
        reset_signing_request_state(this);
    },
    can_navigate_to_step(step_number) {
        return get_step_navigation_rule(this, step_number);
    },
    go_to_signers_order() {
        if (!this.selected_template) {
            this.show_info("Choose a signing template before configuring signers.");
            return;
        }
        this.current_request_step = 2;
    },
    go_to_document_preview() {
        this.current_request_step = 1;
    },
    go_to_signers_from_review() {
        this.current_request_step = 2;
    },
    async go_to_review_send(force_reload_preview = false) {
        if (!this.selected_template) {
            this.show_info("Choose a signing template before continuing.");
            return;
        }
        this.current_request_step = 3;
        await this.ensure_populated_document_preview(force_reload_preview);
    },
    async go_to_step(step_number) {
        if (should_skip_request_step_navigation(this, step_number)) {
            return;
        }
        await open_selected_request_step(this, step_number);
    },
    handle_signers_configured(summary) {
        this.signer_configuration_summary = summary || null;
        this.populated_preview_url = "";
        this.populated_preview_external_url = "";
        this.populated_preview_form_src = "";
        this.populated_preview_token = "";
        this.populated_preview_error = "";
    },
    async handle_signers_continue() {
        const signer_step = this.$refs.configureSignersStep;
        if (!signer_step || typeof signer_step.submit_configuration !== "function") {
            this.show_error("The signer configuration step is not ready yet.");
            return;
        }

        const did_save = await signer_step.submit_configuration();
        if (did_save) {
            await this.go_to_review_send(true);
        }
    },
    async ensure_populated_document_preview(force_reload_preview = false) {
        if (!force_reload_preview && this.populated_preview_url) {
            return;
        }
        await this.load_populated_document_preview();
    },
    async load_populated_document_preview() {
        this.populated_preview_url = "";
        this.populated_preview_external_url = "";
        this.populated_preview_form_src = "";
        this.populated_preview_token = "";
        this.populated_preview_error = "";
        this.populated_preview_loading = true;
        show_system_loader("Preparing signing preview...");
        try {
            const result = await create_populated_document_preview(this);
            this.populated_preview_url = result.populated_preview_url;
            this.populated_preview_external_url = result.populated_preview_external_url || "";
            this.populated_preview_form_src = result.populated_preview_form_src || "";
            this.populated_preview_token = result.populated_preview_token || "";
            this.populated_preview_error = result.populated_preview_error;
        } catch (error) {
            const response_payload = this.parse_response_data(error?.response?.data);
            this.populated_preview_error = response_payload?.message || error?.message || "Failed to load the populated document preview.";
        } finally {
            this.populated_preview_loading = false;
            hide_system_loader_after_render(this);
        }
    },
    async handle_send_contract() {
        if (!this.can_manage_signing_requests) {
            this.show_error("Only staff can send documents for signing.");
            return;
        }
        if (!this.ready_to_send_contract) {
            this.show_error("The signing request is missing workflow or entity profile context.");
            return;
        }

        this.sending_contract = true;
        show_system_loader("Sending contract for signing...");
        try {
            const response = await axios.post(SEND_CONTRACT_URL, build_send_contract_payload(this), get_request_config());
            const parsed_response = this.parse_response_data(response.data);
            if (has_failed_response(parsed_response)) {
                this.show_error(get_send_contract_failure_message(parsed_response));
                return;
            }
            this.show_success(get_send_contract_success_message(parsed_response));
            this.close_new_signing_request();
            await this.load_sent_contracts();
        } catch (error) {
            this.show_error(resolve_send_contract_error(this, error));
        } finally {
            this.sending_contract = false;
            hide_system_loader_after_render(this);
        }
    }
};
